/**
 * AdminAnomalies.js
 *
 * Şüpheli aktivite (anomaly) dashboard'u.
 * Filtre: status, severity, rule, search.
 * Aksiyon: Detay modal | Görmezden Gel | Hesabı Dondur (2-adım) | Oturumları Sonlandır.
 *
 * Route: /admin/anomalies
 */

import { useState, useDeferredValue, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  Eye,
  XCircle,
  Lock,
  LogOut,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  DataTable,
  FilterBar,
  PageHeader,
  ActionMenu,
  EmptyState,
  LoadingState,
} from '../components/admin';
import DangerousConfirmDialog from '@/components/admin/DangerousConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useAnomalies, useReviewAnomaly, useUserAccessPattern } from '../api/security';

// ── Severity badge ────────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  low:    { bg: 'bg-gray-100 text-gray-500',   dot: 'bg-gray-400'   },
  medium: { bg: 'bg-amber-50 text-amber-600',  dot: 'bg-amber-400'  },
  high:   { bg: 'bg-rose-50 text-rose-600',    dot: 'bg-rose-500'   },
};

function SeverityBadge({ severity, t }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.low;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {t(`admin.security.anomalies.severity.${severity}`) || severity}
    </span>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  open:       'bg-rose-50 text-rose-600',
  reviewed:   'bg-blue-50 text-blue-600',
  dismissed:  'bg-gray-100 text-gray-400',
};

function StatusBadgeSmall({ status, t }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${STATUS_CONFIG[status] || 'bg-gray-100 text-gray-500'}`}>
      {t(`admin.security.anomalies.status.${status}`) || status}
    </span>
  );
}

// ── AccessPatternChart ────────────────────────────────────────────────────────

function AccessPatternChart({ pattern = [], t }) {
  if (!pattern.length) {
    return (
      <p className="text-xs text-[var(--text-muted)] text-center py-4">
        {t('admin.security.userDetail.accessPattern.noData')}
      </p>
    );
  }

  const maxCount = Math.max(...pattern.map((p) => p.count), 1);

  return (
    <div>
      <p className="text-xs font-bold text-[var(--text-muted)] mb-2 uppercase tracking-wide">
        {t('admin.security.userDetail.accessPattern.title')}
      </p>
      <div className="flex items-end gap-0.5 h-16">
        {Array.from({ length: 24 }, (_, hour) => {
          const entry = pattern.find((p) => p.hour === hour);
          const count = entry?.count || 0;
          const heightPct = Math.round((count / maxCount) * 100);
          return (
            <div
              key={hour}
              className="flex-1 flex flex-col items-center group relative"
            >
              <div
                className="w-full bg-[var(--brand-primary)] rounded-sm opacity-70 group-hover:opacity-100 transition-opacity"
                style={{ height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%` }}
              />
              {/* Tooltip */}
              {count > 0 && (
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#1C1C1C] text-white text-[9px] rounded px-1 py-0.5 whitespace-nowrap z-10">
                  {hour}:00 — {count}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-[var(--text-muted)]">
        <span>00:00</span>
        <span>06:00</span>
        <span>12:00</span>
        <span>18:00</span>
        <span>23:00</span>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

function AnomalyDetailModal({ anomaly, open, onClose, t, lang }) {
  const [showRaw, setShowRaw] = useState(false);
  const { data: patternData, isLoading: patternLoading } = useUserAccessPattern(
    open && anomaly?.user_id ? anomaly.user_id : null
  );

  if (!anomaly) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold text-[var(--text-main)] flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            {t('admin.security.anomalies.detailTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Summary row */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-xl border-2 border-[var(--border-light)]">
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {t('admin.security.activeSessions.colUser')}
              </p>
              <p className="text-xs font-bold">{anomaly.user_email || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {t('admin.security.anomalies.colRule')}
              </p>
              <p className="text-xs font-bold">
                {t(`admin.security.anomalies.rule.${anomaly.rule}`) || anomaly.rule}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {t('admin.security.anomalies.colSeverity')}
              </p>
              <SeverityBadge severity={anomaly.severity} t={t} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {t('admin.security.anomalies.colStatus')}
              </p>
              <StatusBadgeSmall status={anomaly.status} t={t} />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                {t('admin.security.anomalies.colDate')}
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                {anomaly.created_at
                  ? new Date(anomaly.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')
                  : '—'}
              </p>
            </div>
          </div>

          {/* Access pattern chart */}
          <div className="p-3 border-2 border-[var(--border-light)] rounded-xl">
            {patternLoading ? (
              <p className="text-xs text-[var(--text-muted)] text-center py-4">
                {lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}
              </p>
            ) : (
              <AccessPatternChart
                pattern={patternData?.pattern || []}
                t={t}
              />
            )}
          </div>

          {/* Details JSON */}
          <div>
            <button
              onClick={() => setShowRaw((v) => !v)}
              className="flex items-center gap-1 text-xs font-bold text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors"
            >
              {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {t('admin.security.anomalies.rawDetails')}
            </button>
            <AnimatePresence>
              {showRaw && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden mt-2"
                >
                  <pre className="bg-gray-900 text-green-400 text-[10px] p-3 rounded-xl overflow-x-auto max-h-48 font-mono">
                    {JSON.stringify(anomaly.details || anomaly, null, 2)}
                  </pre>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminAnomalies() {
  const { t, lang } = useLanguage();
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('');
  const [rule, setRule] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const deferredSearch = useDeferredValue(search);

  // Detail modal
  const [detailAnomaly, setDetailAnomaly] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Freeze confirm
  const [freezeTarget, setFreezeTarget] = useState(null);
  const [freezeOpen, setFreezeOpen] = useState(false);

  // Data
  const { data, isLoading, isFetching, refetch } = useAnomalies({
    search: deferredSearch,
    status,
    severity,
    rule,
    page,
    limit: LIMIT,
  });

  const anomalies = data?.anomalies || [];
  const total = data?.total || 0;

  // Mutation
  const reviewAnomaly = useReviewAnomaly();

  const handleAction = useCallback(
    async (anomalyId, action, _userEmail) => {
      try {
        await reviewAnomaly.mutateAsync({ id: anomalyId, action });
        const msgMap = {
          dismiss:          lang === 'tr' ? 'Anomali görmezden gelindi' : 'Anomaly dismissed',
          revoke_sessions:  lang === 'tr' ? 'Oturumlar sonlandırıldı' : 'Sessions revoked',
          investigate:      lang === 'tr' ? 'İncelemeye alındı' : 'Marked for investigation',
        };
        toast.success(msgMap[action] || (lang === 'tr' ? 'İşlem tamamlandı' : 'Done'));
      } catch (err) {
        toast.error(getApiErrorMessage(err, lang));
      }
    },
    [reviewAnomaly, toast, lang]
  );

  const handleFreezeConfirm = async () => {
    if (!freezeTarget) return;
    await handleAction(freezeTarget.anomalyId, 'freeze_account', freezeTarget.userEmail);
    setFreezeOpen(false);
    setFreezeTarget(null);
  };

  const openDetail = (row) => {
    setDetailAnomaly(row);
    setDetailOpen(true);
  };

  // DataTable columns
  const columns = [
    {
      key: 'user_email',
      label: t('admin.security.activeSessions.colUser'),
      sortable: true,
      render: (_val, row) => (
        <div className="min-w-0">
          <p className="text-xs font-bold text-[var(--text-main)] truncate">
            {row.user_name || '—'}
          </p>
          <p className="text-[10px] text-[var(--text-muted)] truncate">{row.user_email}</p>
        </div>
      ),
    },
    {
      key: 'rule',
      label: t('admin.security.anomalies.colRule'),
      render: (_val, row) => (
        <span className="text-xs font-semibold text-[var(--text-main)]">
          {t(`admin.security.anomalies.rule.${row.rule}`) || row.rule}
        </span>
      ),
    },
    {
      key: 'severity',
      label: t('admin.security.anomalies.colSeverity'),
      render: (_val, row) => <SeverityBadge severity={row.severity} t={t} />,
    },
    {
      key: 'created_at',
      label: t('admin.security.anomalies.colDate'),
      sortable: true,
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">
          {row.created_at
            ? new Date(row.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')
            : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('admin.security.anomalies.colStatus'),
      render: (_val, row) => <StatusBadgeSmall status={row.status} t={t} />,
    },
    {
      key: '_actions',
      label: '',
      render: (_val, row) => (
        <ActionMenu
          items={[
            {
              label: t('admin.security.action.detail'),
              icon: <Eye size={14} />,
              onClick: () => openDetail(row),
            },
            {
              label: t('admin.security.action.dismiss'),
              icon: <XCircle size={14} />,
              onClick: () => handleAction(row._id || row.id, 'dismiss', row.user_email),
              disabled: row.status !== 'open',
            },
            {
              label: t('admin.security.action.revokeSessions'),
              icon: <LogOut size={14} />,
              onClick: () => handleAction(row._id || row.id, 'revoke_sessions', row.user_email),
            },
            {
              label: t('admin.security.action.freezeAccount'),
              icon: <Lock size={14} />,
              variant: 'danger',
              onClick: () => {
                setFreezeTarget({ anomalyId: row._id || row.id, userEmail: row.user_email });
                setFreezeOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  // FilterBar config
  const filterFields = [
    {
      type: 'search',
      key: 'search',
      placeholder: t('admin.security.activeSessions.filterSearch'),
      value: search,
      onChange: (v) => { setSearch(v); setPage(0); },
    },
    {
      type: 'select',
      key: 'status',
      label: t('admin.security.anomalies.filterStatus'),
      value: status,
      onChange: (v) => { setStatus(v); setPage(0); },
      options: [
        { value: '', label: t('admin.filter_all') },
        { value: 'open', label: t('admin.security.anomalies.status.open') },
        { value: 'reviewed', label: t('admin.security.anomalies.status.reviewed') },
        { value: 'dismissed', label: t('admin.security.anomalies.status.dismissed') },
      ],
    },
    {
      type: 'select',
      key: 'severity',
      label: t('admin.security.anomalies.filterSeverity'),
      value: severity,
      onChange: (v) => { setSeverity(v); setPage(0); },
      options: [
        { value: '', label: t('admin.filter_all') },
        { value: 'low', label: t('admin.security.anomalies.severity.low') },
        { value: 'medium', label: t('admin.security.anomalies.severity.medium') },
        { value: 'high', label: t('admin.security.anomalies.severity.high') },
      ],
    },
    {
      type: 'select',
      key: 'rule',
      label: t('admin.security.anomalies.filterRule'),
      value: rule,
      onChange: (v) => { setRule(v); setPage(0); },
      options: [
        { value: '', label: t('admin.filter_all') },
        { value: 'high_frequency', label: t('admin.security.anomalies.rule.high_frequency') },
        { value: 'multi_ip', label: t('admin.security.anomalies.rule.multi_ip') },
        { value: 'impossible_travel', label: t('admin.security.anomalies.rule.impossible_travel') },
        { value: 'rapid_sequential', label: t('admin.security.anomalies.rule.rapid_sequential') },
        { value: 'off_hours', label: t('admin.security.anomalies.rule.off_hours') },
      ],
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4">
        <PageHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: t('admin.nav.system') },
            { label: t('admin.nav.anomalies') },
          ]}
          title={t('admin.security.anomalies.title')}
          subtitle={t('admin.security.anomalies.desc')}
          icon={<AlertTriangle size={20} />}
          actions={
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
              {lang === 'tr' ? 'Yenile' : 'Refresh'}
            </motion.button>
          }
        />

        <FilterBar fields={filterFields} />

        {isLoading ? (
          <LoadingState message={t('admin.security.anomalies.loading')} />
        ) : anomalies.length === 0 ? (
          <EmptyState
            title={t('admin.security.anomalies.emptyTitle')}
            description={t('admin.security.anomalies.emptyDesc')}
            icon={<AlertTriangle size={32} />}
          />
        ) : (
          <DataTable
            columns={columns}
            data={anomalies}
            pageSize={LIMIT}
            serverPagination
            currentPage={page + 1}
            totalItems={total}
            onPageChange={(p) => setPage(p - 1)}
          />
        )}
      </div>

      {/* Detail modal */}
      <AnomalyDetailModal
        anomaly={detailAnomaly}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        t={t}
        lang={lang}
      />

      {/* Freeze 2-step confirm */}
      <DangerousConfirmDialog
        open={freezeOpen}
        onOpenChange={setFreezeOpen}
        count={1}
        onConfirm={handleFreezeConfirm}
        loading={reviewAnomaly.isPending}
      />
    </AdminLayout>
  );
}
